import type { SlideQuestion } from '@breakoutlearning/firebase-repository/models/SlideQuestion'
import type { RoomStateAnswer } from '@breakoutlearning/firebase-repository/models/RoomStateAnswer'
import { Button } from 'components/Button'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useId } from 'react'
import { CheckIcon } from 'components/icons/Check'
import { XSmallIcon } from 'components/icons/XSmall'
import { Dialog } from 'components/dialogs/Dialog'
import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { LazyMarkdown } from 'components/LazyMarkdown'
import { SlideQuestionType } from '@breakoutlearning/firebase-repository/models/SlideQuestionType'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import {
  getQuizAnswerScore,
  displayedPercentage,
} from '@breakoutlearning/firebase-repository/sources-of-truth/scoring'
import { useTranslationTyped } from 'i18n/i18n'

export const QuizResultsDialog = observer(function QuizResultsDialog({
  removeSelf,
  cubit,
}: {
  removeSelf: VoidFunction
  cubit: StudentAssignmentCubit
}) {
  const { t } = useTranslation()

  const preWorkQuestionsMap: Record<string, SlideQuestion> =
    cubit.preWorkQuestions.reduce(
      (map, item) => {
        map[item.id] = item
        return map
      },
      {} as Record<string, SlideQuestion>
    )

  // todo: zod & strict typing
  const preWorkAnswers = cubit.answers.models

  const answersWithQuestions: {
    answer: RoomStateAnswer
    question: SlideQuestion
  }[] = []

  // join answers with questions via questionId
  for (const answer of preWorkAnswers) {
    const qid = answer.data.slideQuestionId
    const question = preWorkQuestionsMap[qid]
    if (!question) continue
    answersWithQuestions.push({ answer: answer, question: question })
  }

  const headingId = useId()

  return (
    <Dialog testId="quiz-results-dialog" size="lg" aria-labelledby={headingId}>
      <DialogCloseButton />
      <div className="relative flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <h1 id={headingId} className="text-headline-large">
            {t('student_assignment.quiz_results')}
          </h1>
          <div className="text-headline-medium">
            {displayedPercentage(cubit.preWorkQuizPercentage)}%
          </div>
        </div>
        <ul className="flex flex-grow flex-col overflow-y-auto">
          {answersWithQuestions
            .sort((a, b) => {
              return a.question.data.question.localeCompare(
                b.question.data.question,
                undefined,
                {
                  numeric: true,
                }
              )
            })
            .map((ansWithQuestion, index) => (
              <QuizResultListItem
                key={index}
                index={index}
                question={ansWithQuestion.question}
                answer={ansWithQuestion.answer}
              />
            ))}
        </ul>
        {/* idk why this button needs to be in a div it shrinks otherwise*/}
        <div className="h-[3.25rem]">
          <Button
            testId="quiz-results-dialog-exit-portal-button"
            className="mt-[1.25rem] w-full rounded-2xl bg-core-primary text-core-on-primary"
            onClick={removeSelf}
          >
            {t('student_assignment.exit_portal')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
})

const QuizResultListItem = ({
  question,
  answer: answerDoc,
}: {
  index: number
  question: SlideQuestion
  answer: RoomStateAnswer
}) => {
  const answer = answerDoc.data

  // BL-4382: index into the authored (DB) `answers` array using the persisted DB
  // index (`answer.answer` / `answerList`) — authored order, not the shuffle.
  const answerStr =
    answer.answerList && answer.answerList.length > 0
      ? answer.answerList.map((a) => question.data.answers[a]).join(', ')
      : question.data.answers[answer.answer]
  const correctAnswer =
    question.data.questionType === SlideQuestionType.sorting
      ? question.data.answers.join(', ')
      : question.data.answers[question.data.correct ?? -1]
  const isGroupQuestion = Boolean(question.data.groupSlideId)
  const { tt } = useTranslationTyped()

  const isCorrect = (() => {
    if (question.data.questionType === SlideQuestionType.multipleChoice) {
      return answer.answer === question.data.correct
    }
    const answerList = answer.answerList || []
    if (answerList.length !== question.data.answers.length) return false
    return answerList.every((ans, index) => ans === index)
  })()

  const scoreAsPercent =
    displayedPercentage(
      getQuizAnswerScore({ answer: answerDoc, question }) * 100
    ) + '%'

  const iconToShow = isCorrect ? (
    <CheckIcon
      size={16}
      color="green"
      role="img"
      aria-label={tt.student_assignment.answer_correct()}
    />
  ) : (
    <XSmallIcon
      size={16}
      color="red"
      role="img"
      aria-label={tt.student_assignment.answer_incorrect()}
    />
  )

  return (
    <li className="border-b-1 border-gray-400 bg-core-tertiary px-4 py-3 pb-3">
      <div className="flex flex-row items-center">
        {/* icon size isnt consistent unless wrapped in div. not sure why*/}
        <div className="w-[3.5rem] min-w-[3.5rem] text-label-large">
          {question.isSorting ? (
            <>
              <span className="sr-only">{tt.a11y.score()}: </span>
              {scoreAsPercent}
            </>
          ) : (
            iconToShow
          )}
        </div>
        <h2 className="flex-grow text-label-medium">
          {question.data.question}
        </h2>
      </div>
      <div className="markdown-quiz-results ml-[3.5rem] mt-3">
        {/* Sighted users tell this is the answer from the layout */}
        <span className="sr-only">{tt.a11y.your_answer()}:</span>
        <LazyMarkdown>{answerStr}</LazyMarkdown>
      </div>
      {!isCorrect && !isGroupQuestion && (
        <>
          <div className="ml-[3.5rem] mt-3 flex flex-row">
            <CheckIcon
              size={16}
              color="green"
              role="img"
              aria-label={tt.student_assignment.correct_answer()}
            />
            <h3 className="ml-1 text-label-medium">
              {tt.student_assignment.correct_answer()}
            </h3>
          </div>
          <div className="markdown-quiz-results ml-[3.5rem] mt-3">
            <LazyMarkdown>{correctAnswer}</LazyMarkdown>
          </div>
        </>
      )}
    </li>
  )
}
